import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import TimezoneSelect from 'react-timezone-select';
import { BUS_FIELDS, validateBusData, normalizeBusData, useBusFields, getBusStopsList, getTimezoneForCity } from '../busUtils';
import 'react-datepicker/dist/react-datepicker.css';
import './AddBusForm.css';
import { EnhancedSelect } from '@trip-planner-monorepo/shared';
import { useParams } from 'react-router-dom';
import { useAuth } from '@trip-planner-monorepo/shared';

const AddBusForm = ({ onSubmit, initialData = {}, onCancel }) => {
    const { tripId } = useParams();
    const { currentUser } = useAuth();
    const busFields = useBusFields(tripId);

    // Define the list of standard cities in one place for reuse
    const STANDARD_CITIES = [
        { value: 'NYC', label: 'New York', name: 'New York' },
        { value: 'LON', label: 'London', name: 'London' },
        { value: 'CHI', label: 'Chicago', name: 'Chicago' },
        { value: 'LAX', label: 'Los Angeles', name: 'Los Angeles' },
        { value: 'PAR', label: 'Paris', name: 'Paris' },
        { value: 'BER', label: 'Berlin', name: 'Berlin' },
        { value: 'MAD', label: 'Madrid', name: 'Madrid' },
        { value: 'ROM', label: 'Rome', name: 'Rome' },
        { value: 'AMS', label: 'Amsterdam', name: 'Amsterdam' },
        { value: 'BRU', label: 'Brussels', name: 'Brussels' },
        { value: 'ZRH', label: 'Zurich', name: 'Zurich' },
        { value: 'VIE', label: 'Vienna', name: 'Vienna' },
        { value: 'BOS', label: 'Boston', name: 'Boston' },
        { value: 'ATL', label: 'Atlanta', name: 'Atlanta' }
    ];
    
    // Create city options for dropdown
    const cityOptions = [
        ...STANDARD_CITIES,
        // The special option that allows creating custom cities
        {
            value: 'custom',
            label: 'Other (Specify a city)',
            name: 'Other',
            isCustom: true
        }
    ];

    // Helper function to transform city strings to objects for the form
    const transformCityToObject = (cityName) => {
        if (!cityName) return '';
        
        // If it's already an object, return it as is
        if (typeof cityName === 'object') return cityName;
        
        // Try to find a match in standard cities
        const matchingCity = STANDARD_CITIES.find(city => 
            city.name === cityName || 
            city.label === cityName || 
            city.value === cityName
        );
        
        if (matchingCity) {
            return matchingCity;
        }
        
        // If no match, create a custom city object
        return {
            name: cityName,
            label: cityName,
            value: cityName.toLowerCase().replace(/\s+/g, '-'),
            isCustom: true
        };
    };

    // Transform city strings to objects in initial data
    if (initialData.departureCity && typeof initialData.departureCity === 'string') {
        initialData.departureCity = transformCityToObject(initialData.departureCity);
    }

    if (initialData.arrivalCity && typeof initialData.arrivalCity === 'string') {
        initialData.arrivalCity = transformCityToObject(initialData.arrivalCity);
    }

    // Helper function to transform operator strings to objects for the form
    const transformOperatorToObject = (operatorName) => {
        if (!operatorName) return '';
        
        // If it's already an object, return it as is
        if (typeof operatorName === 'object') return operatorName;
        
        // Check if it has format "CODE - Name"
        let code = '';
        let name = operatorName;
        
        if (operatorName.includes(' - ')) {
            const parts = operatorName.split(' - ');
            code = parts[0].trim();
            name = parts[1].trim();
        }
        
        // Create a custom operator object
        return {
            name: name,
            label: operatorName,  // Keep the original string as label
            code: code,
            value: code || name.toLowerCase().replace(/\s+/g, '-'),
            isCustom: true
        };
    };

    // Transform operator strings to objects in initial data
    if (initialData.operator && typeof initialData.operator === 'string') {
        initialData.operator = transformOperatorToObject(initialData.operator);
    }

    // Convert date strings to Date objects when initializing from existing data
    const processedInitialData = {
        ...initialData,
        // Handle different field naming between storage and form
        departureDate: initialData.departureTime ? new Date(initialData.departureTime) : null,
        arrivalDate: initialData.arrivalTime ? new Date(initialData.arrivalTime) : null
    };

    // Prepare initial passengers data
    const initialPassengers = initialData.passengers?.length
        ? initialData.passengers.map(p => ({ ...p }))
        : [{ firstName: '', lastName: '', seat: '', foodPreference: 'none' }];

    // Form state initialization
    const [formData, setFormData] = useState({
        operator: initialData.operator || '',
        serviceNumber: initialData.serviceNumber || '',
        departureCity: initialData.departureCity || '',
        departureStop: initialData.departureStop || '',
        arrivalCity: initialData.arrivalCity || '',
        arrivalStop: initialData.arrivalStop || '',
        departureDate: initialData.departureDate || null,
        arrivalDate: initialData.arrivalDate || null,
        departureTime: initialData.departureTime || '',
        arrivalTime: initialData.arrivalTime || '',
        bookingReference: initialData.bookingReference || '',
        bookingAgent: '',
        platform: '',
        seat: '',
        notes: '',
        // Custom city fields
        customDepartureCity: '',
        customArrivalCity: '',
        // Cost details
        cost: {
            amount: 0,
            currency: 'USD',
            paid: false
        },
        // Copy over any provided initial data
        ...processedInitialData,
        // Ensure arrays are properly initialized
        passengers: initialPassengers,
        ticketNumbers: processedInitialData.ticketNumbers || ['']
    });
    
    const [error, setError] = useState('');
    const [busTitle, setBusTitle] = useState('');
    const [operatorOptions, setOperatorOptions] = useState([]);
    const [loadingOperators, setLoadingOperators] = useState(false);
    const [validationState, setValidationState] = useState({ errors: {}, warnings: {} });
    const [touched, setTouched] = useState({});
    
    // Custom input refs for auto-focus
    const customDepartureCityInputRef = useRef(null);
    const customArrivalCityInputRef = useRef(null);

    // Create a mapping of cities to timezones
    const CITY_TIMEZONES = {
        'NYC': 'America/New_York',
        'LON': 'Europe/London',
        'CHI': 'America/Chicago',
        'LAX': 'America/Los_Angeles',
        'PAR': 'Europe/Paris',
        'BER': 'Europe/Berlin',
        'MAD': 'Europe/Madrid',
        'ROM': 'Europe/Rome',
        'AMS': 'Europe/Amsterdam',
        'BRU': 'Europe/Brussels',
        'ZRH': 'Europe/Zurich',
        'VIE': 'Europe/Vienna',
        'BOS': 'America/New_York',
        'ATL': 'America/New_York'
    };

    // Load bus operators
    useEffect(() => {
        const loadOperators = async () => {
            try {
                setLoadingOperators(true);
                // Create a simple list of common bus operators since getBusOperatorsList doesn't exist
                const operators = [
                    { value: 'greyhound', label: 'Greyhound', name: 'Greyhound' },
                    { value: 'megabus', label: 'Megabus', name: 'Megabus' },
                    { value: 'flixbus', label: 'FlixBus', name: 'FlixBus' },
                    { value: 'nationalexpress', label: 'National Express', name: 'National Express' },
                    { value: 'boltbus', label: 'BoltBus', name: 'BoltBus' },
                    { value: 'peterpan', label: 'Peter Pan', name: 'Peter Pan' },
                    { value: 'eurolines', label: 'Eurolines', name: 'Eurolines' },
                    { value: 'coach', label: 'Coach USA', name: 'Coach USA' },
                    { value: 'redcoach', label: 'RedCoach', name: 'RedCoach' },
                    { value: 'ourbus', label: 'OurBus', name: 'OurBus' },
                    // Add more common bus operators as needed
                    { value: 'custom', label: 'Other (Specify an operator)', name: 'Other', isCustom: true }
                ];
                setOperatorOptions(operators);
            } catch (error) {
                console.error('Error loading bus operators:', error);
            } finally {
                setLoadingOperators(false);
            }
        };
        
        loadOperators();
    }, []);
    
    // Ensure passengers is always initialized as an array
    useEffect(() => {
        if (!Array.isArray(formData.passengers)) {
            console.warn('Passengers was not an array, resetting to default');
            setFormData(prev => ({
                ...prev,
                passengers: [{ firstName: '', lastName: '', seat: '', foodPreference: 'none' }]
            }));
        }
    }, [formData.passengers]);
    
    // Generate the title whenever relevant fields change
    useEffect(() => {
        const generatedTitle = generateTitle();
        setBusTitle(generatedTitle);
    }, [
        formData.operator,
        formData.serviceNumber,
        formData.departureCity,
        formData.departureStop,
        formData.arrivalCity,
        formData.arrivalStop,
        formData.departureDate
    ]);

    // Run validation whenever form data changes
    useEffect(() => {
        // Run validation without triggering state updates if nothing changed
        const validation = validateBusData(formData);
        if (JSON.stringify(validation) !== JSON.stringify(validationState)) {
            setValidationState(validation);
        }
    }, [formData]);

    // Handle input changes
    const handleInputChange = (field, value) => {
        setTouched(prev => ({ ...prev, [field]: true }));
        
        console.log(`Handling input change for ${field}:`, value);
        
        if (field.includes('.')) {
            // Handle nested fields (e.g., cost.amount)
            const [parent, child] = field.split('.');
            if (parent === 'passengers') {
                // Special handling for passenger fields to ensure it's an array
                // Format: passengers.0.firstName
                const match = field.match(/passengers\.(\d+)\.(.+)/);
                if (match) {
                    const [_, index, passengerField] = match;
                    const indexNum = parseInt(index, 10);
                    
                    // Ensure passengers is an array
                    if (!Array.isArray(formData.passengers)) {
                        setFormData(prev => ({
                            ...prev,
                            passengers: [{ firstName: '', lastName: '', seat: '', foodPreference: 'none' }]
                        }));
                        return;
                    }
                    
                    const newPassengers = [...formData.passengers];
                    // Ensure the passenger at this index exists
                    if (!newPassengers[indexNum]) {
                        newPassengers[indexNum] = { firstName: '', lastName: '', seat: '', foodPreference: 'none' };
                    }
                    
                    newPassengers[indexNum] = {
                        ...newPassengers[indexNum],
                        [passengerField]: value
                    };
                    
                    setFormData(prev => ({
                        ...prev,
                        passengers: newPassengers
                    }));
                    return;
                }
            } else {
                // Handle other nested fields (e.g., cost.amount)
                setFormData(prev => ({
                    ...prev,
                    [parent]: {
                        ...prev[parent],
                        [child]: value
                    }
                }));
            }
        } else if (field === 'departureCity') {
            setFormData(prev => ({
                ...prev,
                departureCity: value
            }));
            
            // Auto-set timezone based on city
            if (value && value.value && CITY_TIMEZONES[value.value]) {
                const timezone = { value: CITY_TIMEZONES[value.value], label: CITY_TIMEZONES[value.value] };
                setFormData(prev => ({
                    ...prev,
                    departureTimezone: timezone
                }));
            }
        } else if (field === 'arrivalCity') {
            setFormData(prev => ({
                ...prev,
                arrivalCity: value
            }));
            
            // Auto-set timezone based on city
            if (value && value.value && CITY_TIMEZONES[value.value]) {
                const timezone = { value: CITY_TIMEZONES[value.value], label: CITY_TIMEZONES[value.value] };
                setFormData(prev => ({
                    ...prev,
                    arrivalTimezone: timezone
                }));
            }
        } else {
            // Handle regular fields
            setFormData(prev => ({
                ...prev,
                [field]: value
            }));
        }
        
        // If there was an error for this field, clear it
        if (error) {
            setError('');
        }
    };

    // Handle date changes
    const handleDateChange = (field, date) => {
        setFormData(prev => ({
            ...prev,
            [field]: date || null
        }));

        // Validate dates if necessary
        if (field === 'departureDate' || field === 'arrivalDate') {
            validateDates(field, date);
        }
    };

    // Check if a passenger has any data entered
    const hasPassengerData = (passenger) => {
        // Safety check to ensure passenger is an object
        if (!passenger || typeof passenger !== 'object') return false;
        
        return passenger.firstName || 
               passenger.lastName || 
               passenger.seat || 
               (passenger.foodPreference && passenger.foodPreference !== 'none');
    };

    // Add passenger
    const addPassenger = () => {
        // Ensure passengers is an array
        if (!Array.isArray(formData.passengers)) {
            setFormData(prev => ({
                ...prev,
                passengers: [{ firstName: '', lastName: '', seat: '', foodPreference: 'none' }]
            }));
            return;
        }
        
        // Check if the last passenger has any data entered
        const lastPassengerIndex = formData.passengers.length - 1;
        const lastPassenger = formData.passengers[lastPassengerIndex];
        
        // Only allow adding if the last passenger has some data
        if (hasPassengerData(lastPassenger)) {
            setFormData(prev => ({
                ...prev,
                passengers: [...prev.passengers, {
                    firstName: '',
                    lastName: '',
                    seat: '',
                    foodPreference: 'none'
                }]
            }));
        } else {
            // Optionally show a message that the current passenger needs data
            alert("Please enter information for the current passenger before adding another.");
        }
    };

    // Remove passenger
    const removePassenger = (index) => {
        // Ensure passengers is an array
        if (!Array.isArray(formData.passengers)) {
            setFormData(prev => ({
                ...prev,
                passengers: [{ firstName: '', lastName: '', seat: '', foodPreference: 'none' }]
            }));
            return;
        }
        
        const newPassengers = [...formData.passengers];
        newPassengers.splice(index, 1);
        
        // Ensure we always have at least one passenger
        if (newPassengers.length === 0) {
            newPassengers.push({ firstName: '', lastName: '', seat: '', foodPreference: 'none' });
        }
        
        setFormData(prev => ({
            ...prev,
            passengers: newPassengers
        }));
    };

    // Clear the first passenger's fields
    const clearFirstPassenger = () => {
        // Ensure passengers is an array
        if (!Array.isArray(formData.passengers)) {
            setFormData(prev => ({
                ...prev,
                passengers: [{ firstName: '', lastName: '', seat: '', foodPreference: 'none' }]
            }));
            return;
        }
        
        setFormData(prev => ({
            ...prev,
            passengers: [
                {
                    firstName: '',
                    lastName: '',
                    seat: '',
                    foodPreference: 'none'
                },
                ...prev.passengers.slice(1) // Keep all other passengers
            ]
        }));
    };

    // Add ticket number
    const addTicketNumber = () => {
        const newTickets = [...formData.ticketNumbers, ''];
        setFormData(prev => ({
            ...prev,
            ticketNumbers: newTickets
        }));
    };

    // Remove ticket number
    const removeTicketNumber = (index) => {
        const newTickets = [...formData.ticketNumbers];
        newTickets.splice(index, 1);
        setFormData(prev => ({
            ...prev,
            ticketNumbers: newTickets
        }));
    };

    // Handle ticket number change
    const handleTicketNumberChange = (index, value) => {
        const newTickets = [...formData.ticketNumbers];
        newTickets[index] = value;
        setFormData(prev => ({
            ...prev,
            ticketNumbers: newTickets
        }));
    };

    // Validate dates
    const validateDates = (changedField, newDate) => {
        const departureDate = changedField === 'departureDate' ? newDate : formData.departureDate;
        const arrivalDate = changedField === 'arrivalDate' ? newDate : formData.arrivalDate;
        
        if (departureDate && arrivalDate) {
            if (departureDate > arrivalDate) {
                setError('Departure date cannot be after arrival date');
            } else {
                setError('');
            }
        }
    };

    // Define getStringValue to safely extract string values
    const getStringValue = (field) => {
        if (!field) return '';
        if (typeof field === 'string') return field;
        if (typeof field === 'object') {
            return field.name || field.label || field.value || '';
        }
        return String(field);
    };

    // Generate title from form data
    const generateTitle = () => {
        const operator = getStringValue(formData.operator);
        const serviceNumber = formData.serviceNumber;
        const departureCity = getStringValue(formData.departureCity);
        const arrivalCity = getStringValue(formData.arrivalCity);
        const departureStop = getStringValue(formData.departureStop);
        const arrivalStop = getStringValue(formData.arrivalStop);
        
        let title = '';
        
        // Start with operator if available
        if (operator) {
            title = operator;
            
            // Add service number if available
            if (serviceNumber) {
                title += ` ${serviceNumber}`;
            }
        } else if (serviceNumber) {
            title = `Bus ${serviceNumber}`;
        } else {
            title = 'Bus';
        }
        
        // Add route information
        const departureLocation = departureCity;
        const arrivalLocation = arrivalCity;
        
        if (departureLocation && arrivalLocation) {
            title += `: ${departureLocation} to ${arrivalLocation}`;
        } else if (departureLocation) {
            title += ` from ${departureLocation}`;
        } else if (arrivalLocation) {
            title += ` to ${arrivalLocation}`;
        }
        
        // Add departure date to the title if available
        if (formData.departureDate) {
            const departureDate = formData.departureDate;
            if (departureDate && !isNaN(departureDate.getTime())) {
                const day = departureDate.getDate();
                const month = departureDate.toLocaleString('en-US', { month: 'short' });
                const year = departureDate.getFullYear();
                title += ` (${day} ${month} ${year})`;
            }
        }
        
        return title;
    };

    // Handle paid checkbox change
    const handlePaidChange = (e) => {
        setFormData(prev => ({
            ...prev,
            cost: {
                ...prev.cost,
                paid: e.target.checked
            }
        }));
    };

    // Render field error message if any
    const getFieldError = (fieldName) => {
        return touched[fieldName] ? validationState.errors[fieldName] : null;
    };

    const renderFieldError = (fieldName) => {
        // const error = getFieldError(fieldName);
        // return error && <div className="error-message">{error}</div>;
    };

    // Adjust handleSubmit to normalize data before submission
    const handleSubmit = (e) => {
        e.preventDefault();
        
        try {
            console.log("Attempting to submit bus form with data:", formData);
            
            // Check if we have enough info to create a title
            const hasTitleInfo = formData.operator || 
                formData.serviceNumber || 
                formData.departureCity || 
                formData.arrivalCity || 
                formData.departureStop || 
                formData.arrivalStop;
                
            if (!hasTitleInfo) {
                setError('Please provide some bus information before saving.');
                return;
            }
            
            // Create a deep copy to avoid modifying state
            const normalizedData = JSON.parse(JSON.stringify(formData));
            
            // Process city data from EnhancedSelect
            if (normalizedData.departureCity && typeof normalizedData.departureCity === 'object') {
                normalizedData.departureCity = getStringValue(normalizedData.departureCity);
            }
            
            if (normalizedData.arrivalCity && typeof normalizedData.arrivalCity === 'object') {
                normalizedData.arrivalCity = getStringValue(normalizedData.arrivalCity);
            }
            
            // Normalize operator data
            if (normalizedData.operator && typeof normalizedData.operator === 'object') {
                normalizedData.operator = getStringValue(normalizedData.operator);
            }
            
            // Normalize stop data
            ['departureStop', 'arrivalStop'].forEach(field => {
                if (normalizedData[field] && typeof normalizedData[field] === 'object') {
                    normalizedData[field] = getStringValue(normalizedData[field]);
                }
            });
            
            // Add departureTime and arrivalTime using the preserved Date objects
            if (formData.departureDate instanceof Date) {
                normalizedData.departureTime = formData.departureDate.toISOString();
            }
            
            if (formData.arrivalDate instanceof Date) {
                normalizedData.arrivalTime = formData.arrivalDate.toISOString();
            }
            
            // Add ticketNumbers to the submission
            normalizedData.ticketNumbers = formData.ticketNumbers.filter(t => t && t.trim() !== '');
            
            // Important: For Firestore, replace any undefined values with null or empty string
            const allBusFields = [
                'operator', 'serviceNumber', 'departureCity', 'departureStop', 
                'arrivalCity', 'arrivalStop', 'departureTime', 'arrivalTime',
                'departureDate', 'arrivalDate', 'departureTimezone', 'arrivalTimezone',
                'bookingReference', 'bookingAgent', 'notes'
            ];
            
            allBusFields.forEach(field => {
                if (normalizedData[field] === undefined) {
                    normalizedData[field] = null;
                }
            });
            
            // Clean up any null/empty passengers or ticket arrays
            if (normalizedData.passengers && normalizedData.passengers.length === 0) {
                normalizedData.passengers = null;
            }
            
            if (normalizedData.ticketNumbers && normalizedData.ticketNumbers.length === 0) {
                normalizedData.ticketNumbers = null;
            }
            
            // Add the bus title as the summary field
            normalizedData.summary = generateTitle();
            
            console.log("Submitting normalized data:", normalizedData);
            
            // Clear error before submission
            setError('');
            
            // Submit the data as expected by AddTravel.js
            onSubmit({
                details: normalizedData,
                summary: normalizedData.summary
            });
        } catch (error) {
            console.error('Error submitting bus form:', error);
            setError('An error occurred while saving the bus information. Please try again.');
        }
    };

    // Ensure onCancel is correctly implemented
    const handleCancel = () => {
        onCancel();
    };

    const validateFormData = () => {
        const errors = {};
        // Remove specific field validations
        // if (!formData.operator) errors.operator = 'Bus Operator is required';
        // if (!formData.serviceNumber) errors.serviceNumber = 'Service Number is required';
        // if (!formData.departureStop) errors.departureStop = 'Departure Stop is required';
        // if (!formData.arrivalStop) errors.arrivalStop = 'Arrival Stop is required';
        // if (!formData.departureTime) errors.departureTime = 'Departure Time is required';
        // if (!formData.arrivalTime) errors.arrivalTime = 'Arrival Time is required';
        return errors;
    };

    return (
        <div className="bus-form">
            <div className="bus-title">
                <h1>Bus Details</h1>
                {busTitle ? (
                    <div className="bus-title-display">{busTitle}</div>
                ) : (
                    <div className="bus-title-placeholder">
                        A title for this bus journey will be displayed here as you enter values in the fields below.
                    </div>
                )}
            </div>

            {/* Optional Auto Extract Data button - similar to flight form */}
            <div className="auto-extract">
                <button type="button" onClick={() => console.log("Auto extract not implemented")}>
                    Auto Extract Data
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            <form 
                id="busForm"
                onSubmit={handleSubmit}
            >
                {/* Bus Information Section */}
                <div className="form-section">
                    <h2 className="section-title">Bus Information</h2>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="operator">
                                Bus Operator:
                            </label>
                            <EnhancedSelect
                                options={operatorOptions}
                                onChange={(selected) => handleInputChange('operator', selected)}
                                value={formData.operator}
                                placeholder="Select or type any bus operator"
                                isSearchable={true}
                                allowCustomValues={true}
                            />
                            <small className="field-hint">
                                Select from the list or type any bus operator and press Enter
                            </small>
                            {formData.operator && formData.operator.isCustom && (
                                <small className="custom-input-note">Using custom operator: "{formData.operator.name}"</small>
                            )}
                            {renderFieldError('operator')}
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="serviceNumber">
                                Service Number:
                            </label>
                            <input
                                type="text"
                                id="serviceNumber"
                                className="form-control"
                                value={formData.serviceNumber}
                                onChange={(e) => handleInputChange('serviceNumber', e.target.value)}
                                placeholder="e.g. X90, 747"
                            />
                            {renderFieldError('serviceNumber')}
                        </div>
                    </div>
                </div>

                {/* Departure Information Section */}
                <div className="form-section">
                    <h2 className="section-title">Departure Information</h2>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="departureCity">
                                Departure City
                            </label>
                            <EnhancedSelect
                                options={cityOptions}
                                onChange={(selected) => handleInputChange('departureCity', selected)}
                                value={formData.departureCity}
                                placeholder="Select or type any city name"
                                isSearchable={true}
                                allowCustomValues={true}
                            />
                            <small className="field-hint">
                                Select from the list or type any city name and press Enter
                            </small>
                            {formData.departureCity && formData.departureCity.isCustom && (
                                <small className="custom-input-note">Using custom city: "{formData.departureCity.name}"</small>
                            )}
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="departureStop">
                                Departure Stop/Station:
                            </label>
                            <input
                                type="text"
                                id="departureStop"
                                className="form-control"
                                value={formData.departureStop}
                                onChange={(e) => handleInputChange('departureStop', e.target.value)}
                                placeholder="Enter bus stop or station"
                            />
                            {renderFieldError('departureStop')}
                        </div>
                    </div>
                    
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="departureDate">
                                Departure Date/Time:
                            </label>
                            <DatePicker
                                id="departureDate"
                                selected={formData.departureDate}
                                onChange={(date) => handleDateChange('departureDate', date)}
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={15}
                                dateFormat="MMMM d, yyyy h:mm aa"
                                className="form-control"
                                placeholderText="Select departure date and time"
                            />
                            {renderFieldError('departureTime')}
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="departureTimezone">
                                Departure Timezone:
                            </label>
                            <TimezoneSelect
                                id="departureTimezone"
                                value={formData.departureTimezone}
                                onChange={(tz) => handleInputChange('departureTimezone', tz)}
                                className="timezone-select"
                            />
                        </div>
                    </div>
                    
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="platform">
                                Platform/Gate:
                            </label>
                            <input
                                type="text"
                                id="platform"
                                className="form-control"
                                value={formData.platform}
                                onChange={(e) => handleInputChange('platform', e.target.value)}
                                placeholder="e.g. G5, 12"
                            />
                        </div>
                    </div>
                </div>

                {/* Arrival Information Section */}
                <div className="form-section">
                    <h2 className="section-title">Arrival Information</h2>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="arrivalCity">
                                Arrival City
                            </label>
                            <EnhancedSelect
                                options={cityOptions}
                                onChange={(selected) => handleInputChange('arrivalCity', selected)}
                                value={formData.arrivalCity}
                                placeholder="Select or type any city name"
                                isSearchable={true}
                                allowCustomValues={true}
                            />
                            <small className="field-hint">
                                Select from the list or type any city name and press Enter
                            </small>
                            {formData.arrivalCity && formData.arrivalCity.isCustom && (
                                <small className="custom-input-note">Using custom city: "{formData.arrivalCity.name}"</small>
                            )}
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="arrivalStop">
                                Arrival Stop/Station:
                            </label>
                            <input
                                type="text"
                                id="arrivalStop"
                                className="form-control"
                                value={formData.arrivalStop}
                                onChange={(e) => handleInputChange('arrivalStop', e.target.value)}
                                placeholder="Enter bus stop or station"
                            />
                            {renderFieldError('arrivalStop')}
                        </div>
                    </div>
                    
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="arrivalDate">
                                Arrival Date/Time:
                            </label>
                            <DatePicker
                                id="arrivalDate"
                                selected={formData.arrivalDate}
                                onChange={(date) => handleDateChange('arrivalDate', date)}
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={15}
                                dateFormat="MMMM d, yyyy h:mm aa"
                                className="form-control"
                                placeholderText="Select arrival date and time"
                            />
                            {renderFieldError('arrivalTime')}
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="arrivalTimezone">
                                Arrival Timezone:
                            </label>
                            <TimezoneSelect
                                id="arrivalTimezone"
                                value={formData.arrivalTimezone}
                                onChange={(tz) => handleInputChange('arrivalTimezone', tz)}
                                className="timezone-select"
                            />
                        </div>
                    </div>
                </div>

                {/* Booking Details Section */}
                <div className="form-section">
                    <h2 className="section-title">Booking Details</h2>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="bookingAgent">Booking Agent:</label>
                            <input
                                type="text"
                                id="bookingAgent"
                                className="form-control"
                                value={formData.bookingAgent}
                                onChange={(e) => handleInputChange('bookingAgent', e.target.value)}
                                placeholder="Enter booking agent (e.g., Greyhound, Megabus)"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="bookingReference">Booking Reference:</label>
                            <input
                                type="text"
                                id="bookingReference"
                                className="form-control"
                                value={formData.bookingReference}
                                onChange={(e) => handleInputChange('bookingReference', e.target.value)}
                                placeholder="Enter booking reference code"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="cost.amount">Cost:</label>
                            <div className="cost-input-group">
                                <select
                                    id="cost.currency"
                                    className="currency-select form-control"
                                    value={formData.cost.currency}
                                    onChange={(e) => handleInputChange('cost.currency', e.target.value)}
                                >
                                    <option value="USD">USD</option>
                                    <option value="EUR">EUR</option>
                                    <option value="GBP">GBP</option>
                                    <option value="JPY">JPY</option>
                                    <option value="CAD">CAD</option>
                                    <option value="AUD">AUD</option>
                                    <option value="CHF">CHF</option>
                                    <option value="CNY">CNY</option>
                                </select>
                                <input
                                    type="number"
                                    id="cost.amount"
                                    className="cost-amount form-control"
                                    value={formData.cost.amount}
                                    onChange={(e) => handleInputChange('cost.amount', e.target.value)}
                                    placeholder="Enter amount"
                                    step="0.01"
                                    min="0"
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <div className="paid-checkbox-container">
                                <label htmlFor="paid" className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        id="paid"
                                        checked={formData.cost.paid}
                                        onChange={handlePaidChange}
                                    />
                                    <span className="checkbox-text">Paid</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Ticket Numbers Section */}
                    <div className="form-row">
                        <div className="form-group ticket-numbers-container">
                            <label>Ticket Number(s):</label>
                            <div className="ticket-numbers-list">
                                {formData.ticketNumbers.filter(t => t.trim()).map((ticket, index) => (
                                    <div key={index} className="ticket-number-item">
                                        <span>{ticket}</span>
                                        <button
                                            type="button"
                                            className="ticket-remove-btn"
                                            onClick={() => removeTicketNumber(index)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <div className="ticket-number-add">
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formData.ticketNumbers[formData.ticketNumbers.length - 1] || ''}
                                    onChange={(e) => handleTicketNumberChange(formData.ticketNumbers.length - 1, e.target.value)}
                                    placeholder="Enter ticket number"
                                />
                                <button
                                    type="button"
                                    className="ticket-add-btn"
                                    onClick={addTicketNumber}
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Passenger Information Section */}
                <div className="form-section">
                    <h2 className="section-title">Passenger Information</h2>
                    {Array.isArray(formData.passengers) ? formData.passengers.map((passenger, index) => (
                        <div key={index} className="passenger-container">
                            <h3 className="passenger-title">Passenger {index + 1}</h3>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>First Name:</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={passenger.firstName || ''}
                                        onChange={(e) => handleInputChange(`passengers.${index}.firstName`, e.target.value)}
                                        placeholder="Enter first name"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Last Name:</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={passenger.lastName || ''}
                                        onChange={(e) => handleInputChange(`passengers.${index}.lastName`, e.target.value)}
                                        placeholder="Enter last name"
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Seat:</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={passenger.seat || ''}
                                        onChange={(e) => handleInputChange(`passengers.${index}.seat`, e.target.value)}
                                        placeholder="e.g., 32A, Window"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Food Preference:</label>
                                    <select
                                        className="form-control"
                                        value={passenger.foodPreference || 'none'}
                                        onChange={(e) => handleInputChange(`passengers.${index}.foodPreference`, e.target.value)}
                                    >
                                        <option value="none">None</option>
                                        <option value="vegetarian">Vegetarian</option>
                                        <option value="vegan">Vegan</option>
                                        <option value="gluten-free">Gluten-Free</option>
                                        <option value="kosher">Kosher</option>
                                        <option value="halal">Halal</option>
                                    </select>
                                </div>
                            </div>
                            <div className="passenger-buttons">
                                {formData.passengers.length === 1 && hasPassengerData(passenger) ? (
                                    <button
                                        type="button"
                                        className="clear-button"
                                        onClick={clearFirstPassenger}
                                    >
                                        Clear Fields
                                    </button>
                                ) : formData.passengers.length > 1 ? (
                                    <button
                                        type="button"
                                        className="remove-button"
                                        onClick={() => removePassenger(index)}
                                    >
                                        Remove Passenger
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    )) : (
                        <div className="error-message">
                            Passenger data is corrupted. Resetting...
                            {setTimeout(() => {
                                setFormData(prev => ({
                                    ...prev,
                                    passengers: [{ firstName: '', lastName: '', seat: '', foodPreference: 'none' }]
                                }));
                            }, 500)}
                        </div>
                    )}
                    {/* Add passenger button */}
                    <div className="add-button-container">
                        <button
                            type="button"
                            className="add-button"
                            onClick={addPassenger}
                        >
                            + Add Another Passenger
                        </button>
                    </div>
                </div>

                {/* Additional Information Section */}
                <div className="form-section">
                    <h2 className="section-title">Additional Information</h2>
                    <div className="form-row">
                        <div className="form-group full-width">
                            <label htmlFor="notes">
                                Notes:
                            </label>
                            <textarea
                                id="notes"
                                className="form-control"
                                value={formData.notes}
                                onChange={(e) => handleInputChange('notes', e.target.value)}
                                placeholder="Any additional information about this bus journey"
                                rows={4}
                            />
                        </div>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="form-actions">
                    <button 
                        type="submit" 
                        className="save-button"
                    >
                        Save Bus Details
                    </button>
                    <button type="button" onClick={handleCancel} className="cancel-button">
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddBusForm;